import {
    Avatar,
    Box,
    Paper,
    Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';

type TooltipItem = {
    name: string;
    value: number | null;
    color: string;
    isHovered: boolean;
};

type Props = {
    x: number;
    date: string;
    items: TooltipItem[];
    mainDomain?: string;
};

export default function ChartTooltip({
    date,
    items,
    mainDomain,
    x,
}: Props) {
    const isFirstPoint = x < 220;

    const formattedDate = (() => {
        try {
            return format(parseISO(date), 'dd MMM yyyy');
        } catch {
            return date;
        }
    })();

    const hasHoveredItem = items.some((item) => item.isHovered);

    return (
        <Paper
            elevation={0}
            sx={{
                position: 'absolute',
                top: 12,
                left: x,
                transform: isFirstPoint
                    ? 'translateX(0)'
                    : 'translateX(-50%)',
                width: 320,
                borderRadius: '16px',
                overflow: 'hidden',
                bgcolor: '#FFFFFF',
                zIndex: 1000,
                pointerEvents: 'none',

                border: '1px solid #e2e4e7',
                boxShadow:
                    'box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.20)',
            }}
        >
            <Box>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: 32,
                        padding: ' 14px 16px 0 16px',

                    }}
                >
                    <Typography
                        sx={{
                            color: '#333C47',
                            fontFamily: 'Public Sans',
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: '100%',
                        }}
                    >
                        Competitors
                    </Typography>

                    <Typography
                        sx={{
                            color: '#637381',
                            textAlign: 'right',
                            fontFamily: '"Public Sans", sans-serif',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '100%',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                </Box>

                {/* Items */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '12px',
                        gap: '8px',
                    }}
                >
                    {items.map((item) => {
                        const isMainDomain = item.name === mainDomain;

                        return (
                            <Box
                                key={item.name}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    opacity: 1,
                                }}
                            >
                                {/* Series color */}
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        mr: '16px',
                                        bgcolor: item.color,
                                        borderRadius: '3px',
                                        flexShrink: 0,
                                    }}
                                />

                                {/* Favicon */}
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        mr: '12px',
                                        borderRadius: '50%',
                                        bgcolor: isMainDomain
                                            ? 'rgba(255, 86, 0, 0.08)'
                                            : 'rgba(145, 158, 171, 0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Avatar
                                        src={`https://www.google.com/s2/favicons?domain=${item.name}&sz=64`}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: 'transparent',
                                        }}
                                    />
                                </Box>

                                {/* Domain */}
                                <Typography
                                    noWrap
                                    sx={{
                                        minWidth: 0,
                                        flex: 1,
                                        color: '#333C47',
                                        fontFamily: '"Public Sans", sans-serif',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '22px',
                                    }}
                                >
                                    {item.name}
                                </Typography>
                                {/* Value */}
                                <Typography
                                    sx={{
                                        ml: '16px',
                                        minWidth: 54,
                                        flexShrink: 0,
                                        textAlign: 'right',
                                        color: '#637381',
                                        fontFamily: '"Public Sans", sans-serif',
                                        fontVariantNumeric: 'lining-nums tabular-nums',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        lineHeight: '22px',
                                    }}
                                >
                                    {item.value == null
                                        ? '-'
                                        : `${item.value.toFixed(0)}%`}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Paper>
    );
}